import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { EASE } from "@/components/Reveal";
import { workPageItems } from "@/data";

const Tile = ({ item, index }) => {
  const inner = (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: EASE, delay: (index % 2) * 0.08 }}
      className="group relative h-full flex flex-col"
    >
      {/* Image frame */}
      <div
        className={`relative overflow-hidden aspect-[4/3] flex items-center justify-center ${
          item.cta ? "bg-[var(--cerra-fg)]" : "bg-neutral-100 border border-[var(--cerra-border)]"
        }`}
      >
        {item.img && !item.cta && (
          <img
            src={item.img}
            alt={item.title}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
        )}
        {item.video && !item.cta && (
          <video
            data-testid={`work-video-${index}`}
            src={item.video}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
        {item.cta && item.img && (
          <>
            <img
              src={item.img}
              alt={item.title}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/45 group-hover:bg-black/30 transition-colors duration-500" />
          </>
        )}
        {!item.img && !item.video && (
          <span
            className={`text-[10px] font-bold uppercase tracking-[0.28em] ${
              item.cta ? "text-white/40" : "text-black/25"
            }`}
          >
            {item.cta ? "Skunk Machine" : "Image coming soon"}
          </span>
        )}
        {item.cta && (
          <span className="relative z-10 text-[10px] font-bold uppercase tracking-[0.28em] text-white/90">
            Skunk Machine
          </span>
        )}
        <span
          className={`absolute top-4 left-4 font-head text-xs font-medium tracking-widest z-10 ${
            item.cta ? "text-white/80" : item.img ? "text-white/80 mix-blend-difference" : "text-black/40"
          }`}
        >
          {item.n}
        </span>
        <span
          className={`absolute top-4 right-4 p-2 transition-colors duration-300 z-10 ${
            item.cta
              ? "bg-[var(--orange)] text-white"
              : "text-black/0 group-hover:text-black/50"
          }`}
        >
          <ArrowUpRight size={16} />
        </span>
      </div>

      <div className="pt-6 pb-2">
        <h3 className="font-head text-xl sm:text-2xl font-medium uppercase kern-tight leading-tight group-hover:text-[var(--orange)] transition-colors duration-300">
          {item.title}
        </h3>
        <p className="mt-3 text-sm sm:text-base leading-relaxed text-black/60 max-w-xl">
          {item.desc}
        </p>
      </div>
    </motion.div>
  );

  const testId = `work-tile-${index}`;

  return item.link ? (
    <a
      data-testid={testId}
      href={item.link}
      target="_blank"
      rel="noopener noreferrer"
    >
      {inner}
    </a>
  ) : (
    <div data-testid={testId}>{inner}</div>
  );
};

const WorkPage = () => (
  <div data-testid="work-page" className="bg-white min-h-screen">
    <Navbar />
    <section className="pt-36 sm:pt-44 pb-24 sm:pb-32 px-5 sm:px-8">
      <div className="mx-auto max-w-[1600px]">
        <div className="border-b border-[var(--cerra-border)] pb-10 mb-16">
          <span className="text-[var(--orange)] text-xs font-bold uppercase tracking-[0.28em]">
            ✦ Projects
          </span>
          <h1 className="mt-5 font-head text-4xl sm:text-6xl lg:text-7xl font-medium uppercase kern-tight leading-none">
            Work
          </h1>
          <p className="mt-6 max-w-xl text-black/60">
            Designed by Cerra — from first sketch to final fabricated form. A
            selection of styling, scale-model and build projects.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-16">
          {workPageItems.map((item, i) => (
            <Tile key={item.title} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
    <Footer />
  </div>
);

export default WorkPage;
